import EmptyBox from "$root/assets/images/glyphs[preload]/EmptyBox.svg";
import { IconWallBase } from "$root/components/IconWall";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { userClassificationsMap$ } from "$root/functional-areas/instruments/user-classifications";
import { useUndoRedoShortcut } from "$root/hooks/useUndoRedoShortcut";
import { seconds } from "$root/utils";
import { AgGridWithModules, baseGridTheme } from "$root/third-party-integrations/ag-grid/ag-grid";
import { Card } from "$root/widgets-architecture/layout/Card";
import { Column, Icon, Row, Text } from "@mdotm/shadcn/legacy/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { getDefaultStore, readAtom, selectAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useDebounced } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSortFnFor, fuzzyFilterSort, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { Button, type SelectOption } from "@mdotm/shadcn/mdotm/components";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { AgGridEvent, CellValueChangedEvent } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import * as Immutable from "immutable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type PortfolioLabInvestmentItem, PortfolioLabContext } from "../../ctx";
import { useCompositionEditor } from "../../hooks/useCompositionEditor";
import { useInvestmentLab } from "../../hooks/useInvestmentLab";
import { useLabSession } from "../../hooks/useLabSession";
import { useRealInvestmentStatusSync } from "../../hooks/useRealInvestmentStatusSync";
import { COMPOSITION_GROUP_BY_UNGROUP, type CompositionGroupBy } from "../../state";
import { getCompositionPickerConfigOptions } from "../investment-picker";
import type { InvestmentPickerButtonProps } from "../investment-picker/InvestmentPickerButton";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { labGroupCellContextMenuItems } from "./cell/LabGroupCellRenderer";
import { createMergedColumnDefs, type MergedColumnContext, mergedCompositionAutoGroupColumnDef } from "./columns";
import { columnDefsEqual, compositionEqual, deriveCompositionViewMode } from "./grid-sync";
import { CompositionToolbar } from "./header";
import { syncComposition } from "./persistence";
import {
	buildCompositionRows,
	buildMergedCompositionRows,
	CompositionViewMode,
	type MergedCompositionRow,
} from "./rows";
import { collectSearchableInstruments, doesRowPass, searchableTextFor } from "./search";
import { resolveWeightEdit } from "./weight-edit";

/** Stable empty match set so the grid filter callbacks keep a steady identity when no search is active. */
const EMPTY_TICKERS: ReadonlySet<string> = new Set();

/** Overlay shown when an active instrument search matches nothing in the loaded composition (FR-009). */
const NO_MATCH_OVERLAY = `<span class="text-[12px] text-(--palette-N400)">We couldn't find any matches. Try a different search term or clear your filters.</span>`;

const compositionPickerConfigOptions = getCompositionPickerConfigOptions();
function InvestmentEmptyWall(): React.JSX.Element {
	const investmentLab = useInvestmentLab();

	const onInvestmentSubmit = useCallback<InvestmentPickerButtonProps["onSubmitAsync"]>(
		async (investments) => {
			const investmentsCtx = await investmentLab.createInvestments(investments);
			await investmentLab.reloadInvestmentsMetrics(investmentsCtx);
		},
		[investmentLab.reloadInvestmentsMetrics, investmentLab.createInvestments],
	);

	return (
		<IconWallBase>
			<Column gap={24} alignItems="center">
				<img alt="illustration" src={EmptyBox} style={{ width: 103, height: 101 }} />
				<Column gap={4} className="items-center text-center">
					<Text type="Body/L/Bold">Load a composition to start</Text>
					<Text type="Body/S/Book">Select one or more compositions to edit, compare and analyse.</Text>
				</Column>
				<Column gap={8} className="w-full max-w-sm">
					{compositionPickerConfigOptions.map((config) => (
						<Button
							key={config.type}
							palette="secondary"
							className="w-full text-left"
							onClick={() =>
								config.spawn({
									onSubmitAsync: async (entities) => onInvestmentSubmit({ type: config.type, items: entities }),
								})
							}
						>
							<Row gap={8} alignItems="center">
								<Icon icon={config.icon} size={20} />
								<Text type="Body/M/Bold">{config.label}</Text>
							</Row>
						</Button>
					))}
				</Column>
			</Column>
		</IconWallBase>
	);
}

/**
 * The pinned totals row. Every cell on it — the weight and monetary totals and the Solve Issue
 * menu — sums its own column from `investments$`, so the row carries no per-investment data and
 * is a constant: AG Grid keeps one pinned node for the life of the grid.
 */
const PINNED_TOTALS_ROWS: MergedCompositionRow[] = [{ path: ["__totals__"], label: "Totals", data: {} }];

const compositionEditorTheme = baseGridTheme.withParams({
	rowGroupIndentSize: 20,
	headerHeight: 32,
	cellHorizontalPadding: 8,
});
const ungroupCompositionEditorTheme = compositionEditorTheme.withParams({ cellWidgetSpacing: 0 });

type CompositionEditorInnerProps = {
	groupBy: CompositionGroupBy;
	groupByOptions: SelectOption<string>[];
	viewMode: CompositionViewMode;
	matchedTickers: ReadonlySet<string>;
	searchActive: boolean;
	searchKey: string;
};

function CompositionEditorInner({
	groupBy,
	groupByOptions,
	viewMode,
	matchedTickers,
	searchActive,
	searchKey,
}: CompositionEditorInnerProps) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const gridRef = useRef<AgGridReact<MergedCompositionRow> | null>(null);
	// Scopes the undo/redo keyboard shortcut to the editor body (see useUndoRedoShortcut below).
	const containerRef = useRef<HTMLDivElement>(null);

	const { undoRedo } = ctx;

	// Controlled rowData (T1/T2): project `investments$` through `compositionEqual` so the array
	// identity — and therefore the derived rowData — only changes when composition-relevant fields do.
	// AG Grid then delta-updates by `getRowId`, preserving expansion/scroll/focus (G1 spike, R1).
	// Created once via useState (same derived-atom-in-a-component pattern as WorkspaceTable's
	// `derivedWorkspaceColumns`): `ctx.investments$` is a stable provider atom, so this projection
	// never needs rebuilding — unlike useMemo, the initializer can't be discarded and re-run.
	const [compositionInvestments$] = useState(() => selectAtom(ctx.investments$, (inv) => inv, compositionEqual));
	const compositionInvestments = useAtomValue(compositionInvestments$);

	// Controlled columnDefs: a second, narrower projection (see `columnDefsEqual`) so defs rebuild
	// exclusively on investment add/remove/rename — never on weight edits or save-status churn.
	const [columnInvestments$] = useState(() => selectAtom(ctx.investments$, (inv) => inv, columnDefsEqual));
	const columnInvestments = useAtomValue(columnInvestments$);
	const columnDefs = useMemo(() => createMergedColumnDefs(columnInvestments), [columnInvestments]);

	// Column layout (width/order/pinned/sort) is stateful: every grid-originated change is
	// mirrored into the session-scoped `compositionColumnState$`, and defs declare `initialWidth`
	// + the grid runs with `maintainColumnOrder`, so a defs rebuild can never clobber the user's
	// latest sizing/ordering. Replaces the manual getColumnState()/applyColumnState() save-restore.
	const columnsPersistence = agExtra.useSyncColStateWithAtom(ctx.compositionColumnState$);

	// Persist the composition "allocation preference" (group-by + column layout) into the lab session.
	// Both `ctx.compositionGroupBy$` and `ctx.compositionColumnState$` feed `toUpstream`'s `grid` (see
	// useLabSession). Debounced to coalesce the burst of `onStateUpdated` events a single resize/reorder
	// gesture emits into one save; `unmountBehavior: "flush"` persists a still-pending change when the
	// editor unmounts (e.g. the last investment is removed) instead of dropping it.
	const { saveLabSession } = useLabSession();
	const { invoke: saveLabSessionDebounced } = useDebounced(saveLabSession, {
		debounceInterval: seconds(1),
		unmountBehavior: "flush",
	});
	useEffect(() => {
		const store = getDefaultStore();
		const unsubGroupBy = store.sub(ctx.compositionGroupBy$, saveLabSessionDebounced);
		const unsubColumns = store.sub(ctx.compositionColumnState$, saveLabSessionDebounced);
		return () => {
			unsubGroupBy();
			unsubColumns();
		};
	}, [ctx.compositionColumnState$, ctx.compositionGroupBy$, saveLabSessionDebounced]);

	// `useSyncColStateWithAtom` only applies atom→grid on *subsequent* changes — apply the persisted
	// column layout once on mount so a reload restores the user's saved widths/order/sort.
	const onGridReady = useCallback(
		(e: AgGridEvent) => {
			columnsPersistence.onGridReady(e);
			const state = readAtom(ctx.compositionColumnState$);
			if (state != null && state.length > 0) {
				e.api.applyColumnState({ state, applyOrder: true });
			}
		},
		[columnsPersistence, ctx.compositionColumnState$],
	);

	const rowData = useMemo(
		() =>
			buildMergedCompositionRows(
				compositionInvestments.map((inv) => ({
					virtualUuid: inv.virtualUuid,
					rows: buildCompositionRows({
						composition: inv.currentComposition,
						initialComposition: inv.initialComposition,
						instrumentPool: inv.tickerInfoPool,
						viewMode,
						classificationUuid: groupBy.secondLevel,
						investmentName: inv.name,
					}),
				})),
			),
		[compositionInvestments, viewMode, groupBy.secondLevel],
	);

	const { onReset, onRemove, onAddPortfolio, onAddInstrument, onUpdateComposition } = useCompositionEditor();

	// Live real-investment status guard (SSE + polling fallback + settle machine).
	useRealInvestmentStatusSync();

	// Undo/redo restore the FULL snapshot — including the `initial*` baselines that Update-AUM
	// rebases (T5) — and persist through the write lane like any other mutation. Handlers are
	// stable across re-renders so the document keydown listener is not re-registered per render.
	const applyHistory = useCallback(
		(direction: "undo" | "redo") => {
			if ((gridRef.current?.api.getEditingCells().length ?? 0) > 0) {
				return;
			}
			const stack = direction === "undo" ? undoRedo.undoStack : undoRedo.redoStack;
			const entry = stack.current[stack.current.length - 1];
			if (!entry) {
				return;
			}
			const currentInvestment = readAtom(ctx.investments$).find((i) => i.virtualUuid === entry.virtualUuid);
			if (!currentInvestment) {
				return;
			}
			const currentState = {
				virtualUuid: entry.virtualUuid,
				composition: currentInvestment.currentComposition,
				aum: currentInvestment.aum,
				initialComposition: currentInvestment.initialComposition,
				initialAum: currentInvestment.initialAum,
			};
			const restoredEntry = direction === "undo" ? undoRedo.undo(currentState) : undoRedo.redo(currentState);
			if (!restoredEntry) {
				return;
			}
			// A step that moves the baseline moves the server's too, so the Δ and Reset survive a reload.
			const baselineMoved =
				restoredEntry.initialComposition !== currentInvestment.initialComposition ||
				restoredEntry.initialAum !== currentInvestment.initialAum;
			writeAtom(ctx.investments$, (items) =>
				items.map((i) =>
					i.virtualUuid === restoredEntry.virtualUuid
						? {
								...i,
								currentComposition: restoredEntry.composition,
								aum: restoredEntry.aum,
								initialComposition: restoredEntry.initialComposition,
								initialAum: restoredEntry.initialAum,
								baselineUnsaved: i.baselineUnsaved || baselineMoved,
							}
						: i,
				),
			);
			// Undo/redo can restore a different AUM (e.g. undoing an Update-AUM); the column header
			// reads `investments$` itself, so the restored AUM label and the sync-error icon repaint
			// without an imperative refresh (which would remount the header — see LabPortfolioGroupHeader).
			void syncComposition(ctx, restoredEntry.virtualUuid);
		},
		[ctx, undoRedo.undo, undoRedo.redo, undoRedo.undoStack, undoRedo.redoStack],
	);
	const onUndoShortcut = useCallback(() => applyHistory("undo"), [applyHistory]);
	const onRedoShortcut = useCallback(() => applyHistory("redo"), [applyHistory]);

	// Scoped to the editor container: Ctrl+Z in the header search (or any input outside the
	// grid) must do native text undo, not composition undo (T5).
	useUndoRedoShortcut(containerRef, { onUndo: onUndoShortcut, onRedo: onRedoShortcut });

	const autoGroupColDef = useMemo(() => {
		const groupByLabel = groupByOptions.find((o) => o.value === groupBy.secondLevel)?.label ?? groupBy.secondLevel;
		const firstLevelLabel = groupBy.firstLevel === "ALLOCATION" ? "Allocation" : "Holdings";
		return {
			...mergedCompositionAutoGroupColumnDef,
			headerName: `${firstLevelLabel} › ${groupByLabel}`,
			headerComponentParams: { viewMode, groupKey: `${groupBy.firstLevel}:${groupBy.secondLevel}` },
		};
	}, [groupBy, groupByOptions, viewMode]);

	const handleCellValueChanged = useCallback(
		(params: CellValueChangedEvent<MergedCompositionRow>) => {
			const investments = readAtom(ctx.investments$);
			const instrumentPool = Immutable.Map(investments.flatMap((inv) => [...inv.tickerInfoPool]));
			const result = resolveWeightEdit({
				colId: params.column.getColDef().colId ?? "",
				newValue: params.newValue,
				row: params.data,
				investments,
				instrumentPool,
				effectiveGroupBy: groupBy.secondLevel,
			});

			if (!result) {
				return;
			}
			const investmentBeforeEdit = investments.find((i) => i.virtualUuid === result.virtualUuid);
			if (investmentBeforeEdit) {
				undoRedo.snapshot({
					virtualUuid: result.virtualUuid,
					composition: investmentBeforeEdit.currentComposition,
					aum: investmentBeforeEdit.aum,
					initialComposition: investmentBeforeEdit.initialComposition,
					initialAum: investmentBeforeEdit.initialAum,
				});
			}

			writeAtom(ctx.investments$, (items) => {
				const next = items.map(
					(i): PortfolioLabInvestmentItem =>
						i.virtualUuid === result.virtualUuid
							? {
									...i,
									currentComposition: result.updatedComposition,
								}
							: i,
				);
				return next;
			});

			// Weight edits show in cells, not the header — and a failed sync flips this column's save
			// status, which the header picks up from `investments$` on its own.
			void syncComposition(ctx, result.virtualUuid);
		},
		[ctx, undoRedo.snapshot, groupBy.secondLevel],
	);

	// Stable grid context: every field is a stable atom or a stable useCallback, so the grid's
	// context is set once and callbacks (header menus, copy/export formatters, editability
	// predicates) read the LIVE atoms at call time. Cell appearance subscribes on its own
	// (LabValueCellRenderer), and so does the column header (LabPortfolioGroupHeader) — the old
	// decimals/saving refreshCells effects and refreshHeader nudges are gone.
	const gridContext = useMemo<MergedColumnContext>(
		() => ({
			decimals$: ctx.decimals$,
			onReset,
			onRemove,
			onAddPortfolio,
			onAddInstrument,
			// The header menu entry takes a `() => void` and drops the promise, so the precondition
			// fetch inside `onUpdateComposition` needs its rejection terminated here.
			onUpdateComposition: unpromisify(onUpdateComposition),
			investments$: ctx.investments$,
		}),
		[ctx.decimals$, ctx.investments$, onReset, onRemove, onAddPortfolio, onAddInstrument, onUpdateComposition],
	);

	// Re-apply the external instrument-search filter whenever the (debounced, trimmed) query changes, and
	// surface a "no matching instruments" overlay when an active search hides every row. Clearing the query
	// (searchKey === "") deactivates the filter and restores the full tree (FR-008, FR-009). The filter
	// callbacks read the `searchActive`/`matchedTickers` props directly (fresh closures each render); AG Grid
	// still needs this imperative onFilterChanged() nudge because external filters have no declarative re-run.
	// biome-ignore lint/correctness/useExhaustiveDependencies: <searchKey is an intentional re-run trigger; the body reads searchActive>
	useEffect(() => {
		const api = gridRef.current?.api;
		if (!api) {
			return;
		}
		api.onFilterChanged();
		if (searchActive && api.getDisplayedRowCount() === 0) {
			api.showNoRowsOverlay();
		} else {
			api.hideOverlay();
		}
	}, [searchKey, searchActive]);

	return (
		<Column className="h-full" innerRef={containerRef}>
			<AgGridWithModules<MergedCompositionRow>
				treeData
				getDataPath={(row) => row.path}
				getRowId={(p) => p.data.path.join("/")}
				autoGroupColumnDef={autoGroupColDef}
				columnDefs={columnDefs}
				// Defs rebuilds (add/remove/rename) must not reorder the surviving columns back to
				// the defs array order — the user's latest order wins; new columns slot in per defs.
				maintainColumnOrder
				pinnedBottomRowData={PINNED_TOTALS_ROWS}
				rowData={rowData}
				getRowStyle={(params) => (params.node.rowPinned === "bottom" ? { fontWeight: 500 } : undefined)}
				groupDefaultExpanded={1}
				theme={viewMode === CompositionViewMode.ungroup ? ungroupCompositionEditorTheme : compositionEditorTheme}
				innerRef={gridRef}
				onCellValueChanged={handleCellValueChanged}
				getContextMenuItems={labGroupCellContextMenuItems}
				context={gridContext}
				stopEditingWhenCellsLoseFocus={true}
				suppressDragLeaveHidesColumns={true}
				isExternalFilterPresent={() => searchActive}
				doesExternalFilterPass={(node) => node.data != null && doesRowPass(node.data, matchedTickers)}
				overlayNoRowsTemplate={NO_MATCH_OVERLAY}
				onGridReady={onGridReady}
				onStateUpdated={columnsPersistence.onStateUpdated}
			/>
		</Column>
	);
}

export function CompositionEditor() {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const classificationsMap = useAtomValue(userClassificationsMap$);
	const investments = useAtomValue(ctx.investments$);
	// The Card's measured root (kept for the widget's own width-aware children).
	const widgetRef = useRef<HTMLDivElement>(null);

	const groupBy = useAtomValue(ctx.compositionGroupBy$);

	// The toolbar's "add portfolio" picker loads investments the same way the page header's does.
	const investmentLab = useInvestmentLab();
	const onAddPortfolio = useCallback<InvestmentPickerButtonProps["onSubmitAsync"]>(
		async (investments) => {
			const investmentsCtx = await investmentLab.createInvestments(investments);
			await investmentLab.reloadInvestmentsMetrics(investmentsCtx);
		},
		[investmentLab.createInvestments, investmentLab.reloadInvestmentsMetrics],
	);

	// Clear the whole lab in one action (equivalent to removing every column). Confirmed first: the
	// virtual copies are deleted with no undo (deletes are not on the undo stack).
	const onResetLab = useCallback(async () => {
		await spawnYesNoDialog({
			header: "Clear the lab?",
			children: "This removes every loaded composition from the lab. Your saved portfolios are not affected.",
			yesButton: "Clear lab",
			// Awaited by the dialog (its submit button shows the pending state until this settles), so the
			// dialog only closes once the server has actually dropped every column. Firing this and
			// closing immediately would let a reload or navigation kill the in-flight DELETEs, and the
			// "cleared" columns would come back on the next lab entry.
			onSubmitAsync: () => investmentLab.removeAllInvestments(),
		});
	}, [investmentLab.removeAllInvestments]);

	// Global instrument search: useSearchable owns the controlled query + debounce; we derive the matched
	// ticker set from the trimmed debounced query via the same fuzzyFilterSort it uses, so leading/trailing
	// whitespace never breaks matching (FR-011). It is a pure view aid — the grid filters in place and never
	// changes weights, monetary weights, or the pinned totals (which are derived from `investments`, FR-010/FR-013).
	const searchableInstruments = useMemo(() => collectSearchableInstruments(investments), [investments]);
	const { query, setQuery, debouncedQuery } = useSearchable({
		collection: searchableInstruments,
		extractFn: searchableTextFor,
	});
	const trimmedQuery = debouncedQuery.trim();
	const searchActive = trimmedQuery.length > 0;
	const matchedTickers = useMemo<ReadonlySet<string>>(
		() =>
			searchActive
				? new Set(fuzzyFilterSort(searchableInstruments, trimmedQuery, searchableTextFor).map((i) => i.tickerName))
				: EMPTY_TICKERS,
		[searchableInstruments, trimmedQuery, searchActive],
	);

	const groupByOptions = useMemo<SelectOption<string>[]>(() => {
		const classOptions = classificationsMap
			? classificationsMap
					.valueSeq()
					.toArray()
					.flatMap((c) =>
						c.fieldType === "TAG" && c.classificationUuid && c.name
							? [{ label: c.name, value: c.classificationUuid }]
							: [],
					)
					.sort(builtInSortFnFor("label"))
			: [];
		return [{ label: "Ungroup", value: COMPOSITION_GROUP_BY_UNGROUP }, ...classOptions];
	}, [classificationsMap]);

	const viewMode = deriveCompositionViewMode(groupBy);

	if (investments.length === 0) {
		return (
			<Card title="Composition">
				<InvestmentEmptyWall />
			</Card>
		);
	}

	return (
		<Card innerRef={widgetRef}>
			<CompositionToolbar
				groupBy={groupBy}
				setGroupBy={(newGroup) => writeAtom(ctx.compositionGroupBy$, newGroup)}
				groupByOptions={groupByOptions}
				query={query}
				onQueryChange={setQuery}
				onAddPortfolio={onAddPortfolio}
				onResetLab={onResetLab}
				widgetRef={widgetRef}
			/>
			<CompositionEditorInner
				groupBy={groupBy}
				groupByOptions={groupByOptions}
				viewMode={viewMode}
				matchedTickers={matchedTickers}
				searchActive={searchActive}
				searchKey={trimmedQuery}
			/>
		</Card>
	);
}
